import { FC } from "react";
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
  Controller,
} from "react-hook-form";
import InputField from "app/components/common/inputs/InputField";
import FormGroup from "app/components/common/inputs/FormGroup";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import SelectField from "app/components/common/inputs/SelectField";
import Checkbox from "app/components/common/inputs/Checkbox";
import {
  HousingStatusOptions,
  stateOfIssueOptions,
} from "app/utils/staticValues";

interface Props {
  control: Control<any>;
  errors: any;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  sameMailingAddress: string;
  residentialAddressValue: boolean;
  housingStatus: string;
  monthsOfStay: string;
  yearsOfStay: string;
  addressStreetName: string;
  addressStateName: string;
  addressPostCodeName: string;
  housingStatusDisabled?: boolean;
}

const DynamicFormFive: FC<Props> = ({
  control,
  errors,
  register,
  setValue,
  setError,
  clearErrors,
  sameMailingAddress,
  residentialAddressValue,
  housingStatus,
  monthsOfStay,
  yearsOfStay,
  addressStreetName,
  addressStateName,
  addressPostCodeName,
  housingStatusDisabled = false,
}) => {
  const getErrorMessage = (fieldName: string) => {
    const fieldNames = fieldName.split(".");
    return fieldNames.reduce((acc, key) => acc && acc[key], errors);
  };

  return (
    <>
      <div className="text-left  flex gap-5 mb-4">
        <Controller
          name={sameMailingAddress}
          control={control}
          // rules={{
          //   required: "Please select Residential Address",
          // }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <div className="flex flex-col">
                <Checkbox
                  id={sameMailingAddress}
                  label="Mailing address same as residential address"
                  checked={value}
                  onChange={() => onChange(!value)}
                  labelClassName="!text-[#313131] sm:mt-[-15px]"
                />
                {error && (
                  <ErrorsMessage title={error.message} className="!text-left" />
                )}
              </div>
            </>
          )}
        />
      </div>

      {!residentialAddressValue && (
        <>
          <FormGroup>
            <AutocompleteLocations
              label="Mailing address"
              FieldName={addressStreetName}
              placeholder="2464 Royal Ln. Mesa, New Jersey 45463"
              register={register}
              setValue={setValue}
              setError={setError}
              required={!residentialAddressValue}
              className="flex-1"
              error={getErrorMessage(addressStreetName)?.message as string}
              stateFieldName={addressStateName}
              postCodeFieldName={addressPostCodeName}
              clearErrors={clearErrors}
            />
            <SelectField
              label="State"
              name={addressStateName}
              control={control}
              options={stateOfIssueOptions}
              placeholder="State"
              error={getErrorMessage(addressStateName)?.message as string}
              required={true}
              className="flex-1"
            />
          </FormGroup>
          <FormGroup>
            <InputField
              label="Post Code"
              name={addressPostCodeName}
              control={control}
              type="number"
              required={true}
              placeholder="1722"
              error={getErrorMessage(addressPostCodeName)?.message as string}
              className="flex-1"
            />
            <SelectField
              label="Housing status"
              name={housingStatus}
              control={control}
              options={HousingStatusOptions}
              placeholder="Please select housing status"
              error={getErrorMessage(housingStatus)?.message as string}
              required={false}
              className="flex-1"
              isDisabled={housingStatusDisabled}
            />
          </FormGroup>
        </>
      )}
      <FormGroup>
        <InputField
          label="Months of stay"
          name={monthsOfStay}
          control={control}
          type="number"
          required={true}
          placeholder="7"
          error={getErrorMessage(monthsOfStay)?.message as string}
          className="flex-1"
          rules={{
            min: {
              value: 0,
              message: "value cannot be negative",
            },
            max: {
              value: 12,
              message: "value can not be greater than 12",
            },
          }}
        />
        <InputField
          label="Years of stay"
          name={yearsOfStay}
          control={control}
          type="number"
          required={true}
          placeholder="17"
          error={getErrorMessage(yearsOfStay)?.message as string}
          className="w-full sm:w-[49%]"
          rules={{
            min: {
              value: 0,
              message: "value can not be less than 0",
            },
            max: {
              value: 100,
              message: "value can not be greater than 100",
            },
          }}
        />
      </FormGroup>
      {residentialAddressValue && (
        <FormGroup>
          <SelectField
            label="Housing status"
            name={housingStatus}
            control={control}
            options={HousingStatusOptions}
            placeholder="Please select housing status"
            error={getErrorMessage(housingStatus)?.message as string}
            required={false}
            className="flex-1"
          />
        </FormGroup>
      )}
    </>
  );
};

export default DynamicFormFive;
